import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Mail } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Account() {
  const { user, refresh } = useAuth();
  const [emailForm, setEmailForm] = useState({ new_email: "", password: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState("");

  const changeEmail = async (e) => {
    e.preventDefault();
    setBusy("email");
    try {
      await api.post("/auth/account/email", emailForm);
      toast.success("Email updated");
      setEmailForm({ new_email: "", password: "" });
      await refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setBusy("pw");
    try {
      await api.post("/auth/account/password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password updated");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="max-w-2xl" data-testid="account-page">
      <h1 className="text-3xl font-black">Account</h1>
      <p className="text-sm text-neutral-500 mt-1">
        Signed in as <b data-testid="account-current-email">{user?.email}</b>
      </p>

      <form onSubmit={changeEmail} className="mt-8 bg-white border border-[#E5E5E5] rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-neutral-400" />
          <h2 className="font-bold">Change email</h2>
        </div>
        <div className="space-y-2">
          <Label>New email</Label>
          <Input type="email" required value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} data-testid="account-email-input" />
        </div>
        <div className="space-y-2">
          <Label>Confirm with current password</Label>
          <Input type="password" required value={emailForm.password} onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })} data-testid="account-email-password-input" />
        </div>
        <Button type="submit" disabled={busy === "email"} className="rounded-full" data-testid="account-email-submit">
          {busy === "email" ? "Updating…" : "Update email"}
        </Button>
      </form>

      <form onSubmit={changePassword} className="mt-4 bg-white border border-[#E5E5E5] rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-neutral-400" />
          <h2 className="font-bold">Change password</h2>
        </div>
        <div className="space-y-2">
          <Label>Current password</Label>
          <Input type="password" required value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} data-testid="account-current-password-input" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>New password</Label>
            <Input type="password" required minLength={6} value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="account-new-password-input" />
          </div>
          <div className="space-y-2">
            <Label>Confirm new password</Label>
            <Input type="password" required minLength={6} value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="account-confirm-password-input" />
          </div>
        </div>
        <Button type="submit" disabled={busy === "pw"} className="rounded-full" data-testid="account-password-submit">
          {busy === "pw" ? "Updating…" : "Update password"}
        </Button>
      </form>
    </div>
  );
}
